import { environment } from '@g99-product-web-ui/shared-environment';

import { encodeId } from './encryption.util';
import { PATHS } from '../constants/widgets.constants';

/**
 * Generates a URL for accessing a specific form for a given business.
 *
 * @param {number} businessID - The unique identifier of the business.
 * @param {number | null} formID - The unique identifier of the form, or null if no form ID is provided.
 *
 * @returns {string} The generated URL containing the business ID and form ID as query parameters.
 */
export function generateFormUrl(
  businessID: number,
  formID: number | null
): string {
  const domain = environment.local.webUrl;
  const path = PATHS.form.htmlPath;
  return `${domain}${path}?bid=${encodeId(businessID)}&fid=${encodeId(formID ?? '')}`;
}

/**
 * Generates the HTML integration code (script and iframe tags) for embedding a form widget on a page.
 *
 * @param {number | null} businessID - The unique identifier of the business, or null if no business ID is provided.
 * @param {number | null} formID - The unique identifier of the form, or null if no form ID is provided.
 * @param {boolean} enableModernUi - Flag indicating whether the form is enableModernUi or not.
 *
 * @returns {string} The HTML code that includes a script tag to load the widget and an iframe tag to display the form.
 */
export function generateIntegrationCode(
  businessID: number | null,
  formID: number | null,
  enableModernUi: boolean
): string {
  const { webUrl: domainUrl } = environment.local;
  const { emrUrl: oldDomainUrl } = environment.local || {};
  const { publicPath } = PATHS;
  const htmlPagePath = PATHS.form.htmlPath;
  const widgetJSPath = PATHS.form.jsPath;

  const scriptTag = `<script src="${oldDomainUrl}${widgetJSPath}"></script>`;

  const iframeSrc = enableModernUi
    ? `${domainUrl}${publicPath}/${encodeId(businessID ?? '')}/${encodeId(formID ?? '')}`
    : `${domainUrl}${htmlPagePath}?bid=${encodeId(businessID ?? '')}&fid=${encodeId(formID ?? '')}`;

  const iframeTag = `<iframe style="height:610px;width:500px;border:0" src="${iframeSrc}" title="Review Form"></iframe>`;

  return `${scriptTag}${iframeTag}`;
}

/**
 * Generates a simple dental self-assessment preview URL.
 *
 * @param {number | string} businessId - The unique identifier of the business.
 * @param {number | null} formId - The unique identifier of the form, or null if not provided.
 *
 * @returns {string} The generated preview URL.
 */
export function generateDentalPreviewUrl(
  businessId: number | string,
  formId: number | null
): string {
  const domain = environment.local.emrUrl;
  const path = PATHS.dentalAssessment.htmlPath;
  return `${domain}${path}?bid=${encodeId(businessId)}&fid=${encodeId(formId ?? '')}&dental=true`;
}
